/***
 *
 *   PROJECT / ORG CONTACTS TABLE
 *   ContactToolbar + ContactsListView chrome over the shared DataTable. Used
 *   on /app/contacts (variant="page") and embedded in project settings.
 *
 *   The three kinds of row this list mixes — a contact on the project, a
 *   pending invitation, and an organisation contact not yet on the project —
 *   used to be distinguishable only by a translated badge string, which the
 *   row actions then compared against to decide what to offer. They now carry
 *   a `kind` key, so the actions (and the new status filter) read the data
 *   rather than the copy.
 *
 *   Add / delete / invite still go through ViewContext.modal (destructive
 *   confirms already map to danger buttons).
 *
 **********/

import React, { useContext, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { useApiContext } from 'app/api';
import {
    Animate,
    Badge,
    Button,
    Card,
    DataTable,
    EmptyState,
    Icon,
    Menu,
    UserAvatar,
    ViewContext,
    useProjectContext,
    useNavigate,
    cn,
} from 'components/lib';
import { useTranslate } from 'app/translations';
import Style from './contacts.tailwind';

const STATUS_VARIANT = {
    registered: 'green',
    invited: 'blue',
    available: 'neutral',
};

function fullName(contact) {
    return [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim();
}

function contactLabel(contact) {
    return contact.kind === 'invited' ? contact.email : fullName(contact) || contact.email;
}

const ProjectContacts = (props) => {
    const { projectUsers, users, variant = 'embed' } = props;
    const isPage = variant === 'page';
    const queryClient = useQueryClient();
    const { callApi, postEvent } = useApiContext();
    const navigate = useNavigate();
    const { _t } = useTranslate();
    const params = useParams();

    const context = useContext(ViewContext);
    const { selectedProject, isAllProjectSelected, projects } = useProjectContext();

    const id = selectedProject?.id || params?.id;
    const { invites, availableContacts } = users?.data?.data || {};

    const [statusFilter, setStatusFilter] = useState('');

    const contacts = useMemo(() => {
        if (!users?.data || !projectUsers?.data) return [];

        const build = (list, kind) =>
            (list || []).map((item) => ({
                id: item?.user_id || item?.id,
                project_id: item?.project_id,
                kind: kind,
                first_name: item?.first_name || '',
                last_name: item?.last_name || '',
                email: item?.email || '',
                position: item?.position || '',
                permission: item?.permission,
                role: item?.role,
                active: kind === 'registered',
                invited: kind === 'invited',
            }));

        return [
            ...build(projectUsers.data?.contacts, 'registered'),
            ...build(
                (invites || []).filter((invite) => invite.permission === 'contact'),
                'invited',
            ),
            ...build(availableContacts, 'available'),
        ];
    }, [projectUsers?.data, invites, availableContacts, users?.data]);

    const { mutate: resendInvite } = useMutation(
        async (data) => {
            await callApi({
                url: '/api/invite?target=contact',
                method: 'post',
                data: { email: data.email },
            });
        },
        {
            onSuccess: (data, variables) => {
                context.notification.show(
                    _t('invite.reSendTo', { email: variables.email }),
                    'success',
                    true,
                );
            },
            onError: (err) => {
                context.handleError(err);
            },
        },
    );

    const { mutate: addProjectUser } = useMutation(
        (data) =>
            callApi({
                url: `/api/project/${id}/user`,
                method: 'POST',
                data,
            }),
        {
            onSuccess: () => {
                projectUsers.refetch();
            },
        },
    );

    function invite() {
        context.modal.show(
            {
                title: _t('contact.add.one'),
                form: {
                    email: {
                        label: _t('table.email'),
                        type: 'text',
                        required: true,
                    },
                    role: {
                        label: _t('role.label'),
                        type: 'select',
                        default: 'contact',
                        options: [
                            { value: 'admin', label: _t('permissions.admin') },
                            { value: 'contact', label: _t('permissions.contact') },
                        ],
                    },
                    ...(isAllProjectSelected && {
                        project_id: {
                            label: _t('project.label.one'),
                            type: 'select',
                            default: id,
                            options: (projects || []).map((project) => ({
                                value: project.id,
                                label: project.name,
                            })),
                        },
                    }),
                },
                buttonText: _t('invite.send'),
                text: _t('invite.sendInfo'),
                url: `/api/invite/${id}?target=contact`,
                method: 'POST',
            },
            () => {
                postEvent('invited_user');
                props.users.refetch();
            },
        );
    }

    function deleteInvite(data) {
        context.modal.show(
            {
                title: _t('invite.delete.label'),
                form: {},
                buttonText: _t('invite.delete.label'),
                text: _t('invite.delete.confirm', { email: data.email }),
                url: `/api/invite/${data.id}?target=contact`,
                method: 'DELETE',
                destructive: true,
            },
            () => {
                context.notification.show(`${_t('invite.wasDeleted')}`, 'success', true);
                props.users.refetch();
            },
        );
    }

    function edit(contact) {
        navigate(`/app/contacts/${contact.id}`, {
            state: { title: contact.first_name },
        });
    }

    function addUser(contact) {
        addProjectUser({ user_id: contact.id, project_id: id });
    }

    function removeFromProject(contact) {
        context.modal.show(
            {
                title: _t('project.contact.remove.label'),
                form: {},
                buttonText: _t('confirm'),
                text: _t('project.contact.remove.text'),
                url: `/api/project/${id}/user`,
                method: 'DELETE',
                destructive: true,
                injectedData: {
                    user_id: contact.id,
                    project_id: id,
                },
            },
            (res) => {
                queryClient.invalidateQueries('projects');
                if (res.status === 200) projectUsers.refetch();
            },
        );
    }

    const scopedToProject = !isAllProjectSelected || Boolean(params?.id);

    function rowActions(contact) {
        const items = [];

        if (contact.kind === 'invited') {
            items.push({
                label: _t('project.contact.invite.resend'),
                icon: 'refresh-cw',
                action: () => resendInvite(contact),
            });
            items.push({ separator: true });
            items.push({
                label: _t('project.contact.invite.delete'),
                icon: 'trash-2',
                action: () => deleteInvite(contact),
            });
            return items;
        }

        items.push({ label: _t('edit'), icon: 'edit-2', action: () => edit(contact) });

        if (scopedToProject) {
            if (contact.kind === 'registered') {
                items.push({ separator: true });
                items.push({
                    label: _t('project.contact.remove.label'),
                    icon: 'user-minus',
                    action: () => removeFromProject(contact),
                });
            } else {
                items.push({
                    label: _t('project.contact.add'),
                    icon: 'user-plus',
                    action: () => addUser(contact),
                });
            }
        }

        return items;
    }

    const StatusBadge = ({ contact }) => (
        <Badge
            text={_t(`table.badge.${contact.kind}`)}
            color={STATUS_VARIANT[contact.kind]}
            className={Style.badge}
        />
    );

    const NameCell = ({ contact }) => (
        <div className={Style.identity}>
            {contact.kind === 'invited' ? (
                <span className={Style.inviteMark}>
                    <Icon image="mail" size={14} color="currentColor" />
                </span>
            ) : (
                <UserAvatar name={fullName(contact) || contact.email} size="sm" />
            )}
            <span className={Style.identityName}>{contactLabel(contact)}</span>
        </div>
    );

    /* The mobile card is itself the row's click target, so the menu has to
       stop the event or opening it would also open the contact.

       `side` flips for the last rows: the table wrapper is a scroll container,
       so a menu that opens downwards off the final row gets clipped by it. */
    const ActionsCell = ({ contact, meta }) => (
        <span onClick={(e) => e.stopPropagation()}>
            <Menu
                align="end"
                side={meta?.count > 3 && meta.index >= meta.count - 2 ? 'top' : 'bottom'}
                width="w-52"
                label={_t('table.actions.more')}
                triggerClassName={Style.menuTrigger}
                trigger={<Icon image="more-horizontal" size={16} color="currentColor" />}
                list={rowActions(contact)}
            />
        </span>
    );

    const columns = useMemo(
        () => [
            {
                key: 'name',
                header: _t('table.name'),
                sortable: true,
                sortValue: (c) => contactLabel(c).toLowerCase(),
                searchValue: (c) => `${fullName(c)} ${c.email}`,
                render: (c) => <NameCell contact={c} />,
            },
            {
                key: 'email',
                header: _t('table.email'),
                sortable: true,
                render: (c) => <span className={Style.secondary}>{c.email}</span>,
            },
            {
                key: 'status',
                header: _t('table.status'),
                sortable: true,
                width: 120,
                sortValue: (c) => _t(`table.badge.${c.kind}`),
                searchValue: (c) => _t(`table.badge.${c.kind}`),
                render: (c) => <StatusBadge contact={c} />,
            },
            !isAllProjectSelected && {
                key: 'project_role',
                header: _t('table.project_role'),
                sortable: true,
                width: 140,
                sortValue: (c) => (c.role ? _t(`permissions.${c.role}`) : ''),
                searchValue: (c) => (c.role ? _t(`permissions.${c.role}`) : ''),
                render: (c) => (
                    <span className={Style.secondary}>
                        {c.role ? _t(`permissions.${c.role}`) : '—'}
                    </span>
                ),
            },
            {
                key: 'position',
                header: _t('table.position'),
                sortable: true,
                width: 160,
                render: (c) => <span className={Style.secondary}>{c.position || '—'}</span>,
            },
            {
                key: 'actions',
                header: <span className="sr-only">{_t('table.action')}</span>,
                actions: true,
                render: (c, meta) => <ActionsCell contact={c} meta={meta} />,
            },
        ],
        // `scopedToProject` and `id` reach the columns indirectly, through the
        // ActionsCell / rowActions closure, so the rule cannot see them — but
        // dropping them would leave the row menu offering the wrong actions
        // after a project switch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [_t, isAllProjectSelected, scopedToProject, id],
    );

    const renderCard = (contact, meta) => (
        <div className={Style.cardRow}>
            {contact.kind === 'invited' ? (
                <span className={Style.inviteMark}>
                    <Icon image="mail" size={14} color="currentColor" />
                </span>
            ) : (
                <UserAvatar name={fullName(contact) || contact.email} size="sm" />
            )}

            <div className={Style.cardBody}>
                <p className={Style.identityName}>{contactLabel(contact)}</p>
                {contact.kind !== 'invited' && <p className={Style.cardEmail}>{contact.email}</p>}
                <div className={Style.cardMeta}>
                    <StatusBadge contact={contact} />
                    {contact.position && <span>{contact.position}</span>}
                    {!isAllProjectSelected && contact.role && (
                        <span>{_t(`permissions.${contact.role}`)}</span>
                    )}
                </div>
            </div>

            <ActionsCell contact={contact} meta={meta} />
        </div>
    );

    const visibleContacts = useMemo(
        () => (statusFilter ? contacts.filter((c) => c.kind === statusFilter) : contacts),
        [contacts, statusFilter],
    );

    const inviteButton = (
        <Button
            variant="accent"
            size="sm"
            icon="plus"
            text={_t('contact.invite.label.one')}
            action={invite}
            className={Style.inviteBtn}
        />
    );

    const table = (
        <DataTable
            search
            searchPlaceholder={_t('contact.search')}
            rows={visibleContacts}
            columns={columns}
            loading={users?.isLoading || projectUsers?.isLoading}
            rowKey={(c) => `${c.kind}-${c.id}`}
            onRowClick={edit}
            isRowClickable={(c) => c.kind !== 'invited'}
            renderCard={renderCard}
            defaultSort={{ key: 'name', dir: 'asc' }}
            actions={inviteButton}
            filters={[
                {
                    key: 'status',
                    label: _t('table.status'),
                    value: statusFilter,
                    onChange: setStatusFilter,
                    options: [
                        { value: '', label: _t('table.filter.allStatuses') },
                        { value: 'registered', label: _t('table.badge.registered') },
                        { value: 'invited', label: _t('table.badge.invited') },
                        { value: 'available', label: _t('table.badge.available') },
                    ],
                },
            ]}
            onResetFilters={() => setStatusFilter('')}
            empty={
                <EmptyState
                    icon="users"
                    title={_t('contact.empty.title')}
                    text={_t('contact.empty.description')}
                    buttonText={_t('contact.invite.label.one')}
                    action={invite}
                />
            }
        />
    );

    const body = (
        <>
            {!isPage && <h3 className={Style.embedTitle}>{_t('contact.manage.label.multiple')}</h3>}
            <div className={cn(Style.body, !isPage && 'mt-4')}>{table}</div>
        </>
    );

    if (isPage) return <div className={Style.page}>{body}</div>;

    return (
        <Animate>
            <Card className={Style.card}>{body}</Card>
        </Animate>
    );
};

export default ProjectContacts;
